import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { PhoneMultiFactorInfo } from '@angular/fire/auth'
import { lastValueFrom } from 'rxjs'
import { MfaMethodType } from '../../_generated/graphql'
import { AuthService, LoginResult } from './auth.service'
import { MfaService } from './mfa.service'
import { UserService, MfaStatus } from './user/user.service'

@Injectable({ providedIn: 'root' })
export class LoginFlowService {
  constructor(
    private auth: AuthService,
    private mfa: MfaService,
    private users: UserService,
    private router: Router,
  ) {}

  async continueAfterFirstFactor(result: LoginResult): Promise<void> {
    this.auth.setLoginResult(result)
    if (result.resolver) {
      window.scrollTo(0, 0)
      if (result.resolver.hints.length >= 2) {
        await this.router.navigate(['/auth/mfa-verify-select'])
        return
      }
      const totp = result.resolver.hints.find(
        (factor) => factor.factorId === 'totp',
      )
      if (totp) {
        this.auth.setLoginResult({ ...result, factorUid: totp.uid })
        await this.router.navigate(['/auth/totp-verify'])
        return
      }
      const hint = result.resolver.hints.find(
        (factor): factor is PhoneMultiFactorInfo => factor.factorId === 'phone',
      )
      if (!hint) {
        await this.fail(
          '認証状態を確認できませんでした。再度ログインしてください。',
        )
        return
      }
      this.auth.setPhoneNumber(hint.phoneNumber)
      this.auth.setVerificationId(
        await this.mfa.sendSmsCode(result.resolver, hint),
      )
      await this.router.navigate(['/auth/phone-setup/verify'])
      return
    }
    if (!result.success) return
    window.scrollTo(0, 0)
    let status
    try {
      status = await lastValueFrom(this.users.getMfaStatus())
    } catch {
      await this.fail(
        'ログインに失敗しました。時間を置いて再度お試しください。',
      )
      return
    }
    if (
      status.methods.some((method) => method.type === MfaMethodType.Passkey)
    ) {
      await this.router.navigate(['/auth/passkey-verify'])
      return
    }
    if (status.requiredForUser) {
      if (status.methods.length === 0) {
        await this.navigateToEnrollment(status)
      } else {
        await this.fail(
          '認証状態を確認できませんでした。再度ログインしてください。',
        )
      }
    } else {
      await this.router.navigateByUrl(this.auth.consumeReturnUrl() || '/home')
    }
  }

  async navigateToEnrollment(status: MfaStatus): Promise<void> {
    await this.router.navigate([
      status.enrollableMethods.some(
        (method) =>
          method === MfaMethodType.Totp || method === MfaMethodType.Passkey,
      )
        ? '/auth/mfa-select'
        : '/auth/phone-setup',
    ])
  }

  private async fail(message: string): Promise<void> {
    await this.auth.signOutPreservingReturnUrl()
    alert(message)
    this.auth.redirectToLogin()
  }
}
